import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useLazyQuery } from 'react-apollo';
import { useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import Input from '../../../components/Input';
import useMe from '../../../hooks/useMe';
import CommentItem from '../../../components/CommentItem';
import CustomHeader from '../../../components/CustomHeader';
import RestrictedContainer from '../../../components/RestrictedContainer';
import './styles.scss';

const getGql = gql`
  query assembly($_id: ID!) {
    assembly(_id: $_id) {
      _id
      description
      condominium
      type
      status
      subjects {
        _id
        title
      }
    }
    condominium {
      name
    }
  }
`;

const commentsGql = gql`
  query comments($subjectId: ID!, $condominiumId: ID!) {
    subjectComments(subjectId: $subjectId, condominiumId: $condominiumId) {
      _id
      text
      createdAt
      replies {
        text
        user {
          name
        }
      }
      user {
        _id
        name
        condominiums {
          unity
        }
      }
    }
  }
`;

const addCommentsGql = gql`
  mutation addComments($subjectId: ID!, $condominiumId: ID!, $text: String!) {
    subjectAddComment(subjectId: $subjectId, condominiumId: $condominiumId, text: $text) {
      text
      user {
        _id
      }
    }
  }
`;

export default function CommentsScreen() {
  const { condominiumId, subjectId, assemblyId } = useParams();
  const [me] = useMe();
  const [data, setData] = useState([]);
  const commentsListRef = useRef();

  const commonArgs = {
    subjectId,
    condominiumId,
  };

  const { data: poolData, stopPolling } = useQuery(commentsGql, {
    skip: !subjectId || !condominiumId,
    pollInterval: 5000,
    variables: commonArgs,
  });

  const [getComments, { data: lazyData, refetch: refetchComments, loading: refetching }] = useLazyQuery(commentsGql, {
    variables: commonArgs,
  });

  const [getAssemblyData, { data: assemblyData }] = useLazyQuery(getGql, {
    onCompleted: () => getComments(),
  });

  const [input, setInput] = useState('');

  const [addCommentFn, { loading: submitting }] = useMutation(addCommentsGql, {
    variables: {
      ...commonArgs,
      text: input,
    },
    onCompleted: () => {
      refetchComments();
      setInput('');
    },
  });

  const submit = e => {
    e.preventDefault();
    if (input.trim().length > 0) return addCommentFn();
    return undefined;
  };

  useEffect(() => {
    commentsListRef.current.scrollTop = commentsListRef.current.scrollHeight;
  }, [data]);

  useEffect(() => {
    setData(poolData);
  }, [poolData]);

  useEffect(() => {
    setData(lazyData);
  }, [lazyData]);

  const reloadComments = () => {
    refetchComments();
  };

  useEffect(() => {
    if (assemblyId && me && condominiumId) {
      getAssemblyData({
        variables: {
          _id: assemblyId,
        },
      });
    }
  }, [assemblyId, me, condominiumId]);

  useEffect(() => {
    const jivo = document.getElementsByClassName('globalClass_ET');
    if (jivo && jivo[0]) jivo[0].classList.add('is-hidden');

    return () => {
      if (jivo && jivo[0]) jivo[0].classList.remove('is-hidden');
      stopPolling();
    };
  }, []);

  const comments = useMemo(() => {
    if (!data) return [];

    return data.subjectComments || [];
  }, [data]);

  const title = useMemo(() => {
    if (!assemblyData) return '';
    const subject = assemblyData.assembly.subjects.reduce(x => x._id === subjectId);

    return subject ? subject.title : '';
  }, [assemblyData]);

  const condominiumName = useMemo(() => {
    if (!assemblyData) return '';
    return assemblyData.assembly ? assemblyData.condominium.name : '';
  }, [assemblyData]);

  return (
    <div className="d-flex flex-column " style={{ height: '100vh' }}>
      <CustomHeader title="Comentários" to="/dashboard" subtitle={condominiumName} description={title} />
      <div style={{ flex: 1, overflow: 'auto' }} ref={commentsListRef}>
        <div>
          {(!comments.length && (
            <div className="d-flex flex-column justify-content-center h-100">Seja o primeiro a comentar (:</div>
          )) ||
            comments.map((comment, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <CommentItem key={`comment-${index}`} comment={comment} refetchComments={reloadComments} />
            ))}
        </div>
      </div>
      <RestrictedContainer permission="comment">
        <div style={{ height: 50 }}>
          <form onSubmit={submit}>
            <div className="input-group input-group-sm mb-3">
              <Input onChange={e => setInput(e.target.value)} value={input} disabled={submitting || refetching} focus />
              <div className="input-group-append">
                <button className="input-group-text" type="submit">
                  {`${submitting || refetching ? 'Enviando' : 'Enviar'}`}
                </button>
              </div>
            </div>
          </form>
        </div>
      </RestrictedContainer>
    </div>
  );
}
